'use client';

/**
 * Adapters that connect the generic `<MediaPicker>` component (which knows
 * nothing about Ballee) to the dancer-media stack:
 *
 *   - `dancerMediaUploadAdapter`  — wraps `uploadDancerMedia` (the 3-step
 *     direct-to-Supabase flow that bypasses Vercel's 4.5 MB body cap) into
 *     the FormData-based `(formData) => Promise<UploadResult>` contract that
 *     MediaUploader/MediaPicker expects.
 *   - `loadDancerMediaLibrary`    — wraps `getDancerMediaAction` and shapes
 *     the rows into `MediaPickerLibraryItem[]`.
 *
 * Kept in the same folder as `upload-dancer-media.ts` so that the upload
 * helper, the picker adapter, and the server actions stay co-located. Other
 * surfaces (apply wizard, application-edit, audition apply-dialog, …) only
 * need to import these two functions to plug into the picker.
 */
import { DancerMediaCategorySchema } from '@kit/artists';
import type { MediaPickerLibraryItem, UploadResult } from '@kit/uploads';

import { getDancerMediaAction } from './server/media-actions';
import {
  uploadDancerMedia,
  type DancerMediaCategory,
} from './upload-dancer-media';

function parseDancerMediaCategory(
  raw: FormDataEntryValue | null,
): DancerMediaCategory | undefined {
  if (typeof raw !== 'string' || !raw) return undefined;
  const result = DancerMediaCategorySchema.safeParse(raw);
  return result.success ? result.data : undefined;
}

/**
 * Derives the `artist_media.media_type` bucket from a file's real MIME type.
 * Used by `applicationFileUploadAdapter` when the apply form does not stamp an
 * explicit `mediaType` — so a dropped image lands in `photo` and a video in
 * `video`, matching the server's per-bucket `ALLOWED_MIME` allowlist instead of
 * being force-bucketed to `document` (BAL-341).
 */
export function deriveMediaTypeFromMime(
  mimeType: string,
  fileName?: string,
): 'headshot' | 'photo' | 'video' | 'document' {
  if (mimeType.startsWith('image/')) return 'photo';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType === 'application/pdf') return 'document';

  // No usable MIME. The old `document` fallthrough was fine while the uploader
  // refused unlabelled files outright, but they now reach here (BAL-747) — and
  // bucketing an unlabelled .jpg as `document` sends it to a PDF-only server
  // gate. The extension is the only signal left, so use it before giving up.
  const extension = fileName?.trim().toLowerCase().split('.').pop() ?? '';
  // Kept in step with MEDIA_ALLOWED_MIME.photo / .video — listing an extension
  // the server does not accept (avif, say) would only move where it is refused.
  if (['jpg', 'jpeg', 'png', 'webp'].includes(extension)) return 'photo';
  if (['mp4', 'mov', 'webm'].includes(extension)) return 'video';
  return 'document';
}

/**
 * Shared upload adapter for application form fields rendered via the
 * `DynamicFormRenderer`. Both `apply-dialog.tsx` (singleton apply path) and
 * `application-edit-view.tsx` (edit path) used to inline near-duplicate
 * versions of this — kept here to keep the contract in one place.
 *
 * An explicit valid `mediaType` in the FormData is honoured. When it is
 * absent or invalid we DERIVE the bucket from the file's real MIME type
 * (`image/*` → `photo`, `video/*` → `video`, `application/pdf` → `document`).
 * Defaulting blindly to `'document'` (the old behaviour) made the server
 * reject every photo/video at apply time, because the server only allows
 * `application/pdf` for the `document` bucket (BAL-341).
 */
export async function applicationFileUploadAdapter(
  formData: FormData,
): Promise<UploadResult> {
  const file = formData.get('file');
  if (!(file instanceof File)) {
    return { success: false, error: 'No file provided' };
  }
  const mediaTypeRaw = formData.get('mediaType');
  const mediaType =
    typeof mediaTypeRaw === 'string' &&
    ['headshot', 'photo', 'video', 'document'].includes(mediaTypeRaw)
      ? (mediaTypeRaw as 'headshot' | 'photo' | 'video' | 'document')
      : deriveMediaTypeFromMime(file.type, file.name);

  const result = await uploadDancerMedia({ file, mediaType });
  if (!result.success) {
    return { success: false, error: result.error };
  }
  return {
    success: true,
    data: {
      id: result.data.id,
      storage_path: result.data.storage_path,
      signed_url: result.data.signed_url,
    },
  };
}

/**
 * Adapter for `<MediaUploader uploadAction>` and `<MediaPicker uploadAction>`.
 *
 * MediaUploader posts a FormData with `file` + arbitrary `additionalData`
 * key/value pairs (we use `mediaType` and `category`). This adapter pulls
 * those out, calls the existing `uploadDancerMedia` direct-upload helper,
 * and re-shapes the result into the `UploadResult` envelope.
 */
export async function dancerMediaUploadAdapter(
  formData: FormData,
): Promise<UploadResult> {
  const file = formData.get('file');
  const mediaTypeRaw = formData.get('mediaType');
  const titleRaw = formData.get('title');
  const descriptionRaw = formData.get('description');
  const categoryRaw = formData.get('category');

  if (!(file instanceof File)) {
    return { success: false, error: 'No file provided' };
  }
  if (
    typeof mediaTypeRaw !== 'string' ||
    !['headshot', 'photo', 'video', 'document'].includes(mediaTypeRaw)
  ) {
    return { success: false, error: 'Invalid media type' };
  }

  const result = await uploadDancerMedia({
    file,
    mediaType: mediaTypeRaw as 'headshot' | 'photo' | 'video' | 'document',
    title: typeof titleRaw === 'string' && titleRaw ? titleRaw : undefined,
    description:
      typeof descriptionRaw === 'string' && descriptionRaw
        ? descriptionRaw
        : undefined,
    category: parseDancerMediaCategory(categoryRaw),
  });

  if (!result.success) {
    return { success: false, error: result.error };
  }

  // MediaUploader's UploadResult expects { id, storage_path, signed_url? }.
  return {
    success: true,
    data: {
      id: result.data.id,
      storage_path: result.data.storage_path,
      signed_url: result.data.signed_url,
    },
  };
}

/**
 * Loads the current user's dancer-media library and shapes it for
 * `<MediaPicker loadLibrary>`. `getDancerMediaAction` already returns rows
 * with signed URLs in `storage_path`, so we just translate field names.
 */
export async function loadDancerMediaLibrary(): Promise<
  MediaPickerLibraryItem[]
> {
  const result = await getDancerMediaAction();
  if (!result.success) {
    throw new Error(result.error);
  }
  return result.data.map((m) => ({
    id: m.id,
    media_type: m.media_type,
    storage_path: m.storage_path,
    external_url: m.external_url,
    title: m.title,
    description: m.description,
    mime_type: m.mime_type,
    file_size_bytes: m.file_size_bytes,
    // `getMediaByUser` already replaces storage_path with the signed URL,
    // so reuse it. MediaThumb's loader chain also accepts the original
    // storagePath if needed.
    signed_url: m.storage_path,
    // Video residency signals — carried so the picker cluster routes a migrated
    // video to Mux/R2 instead of the dead signed URL (BAL-721 apply-preview 400).
    provider: m.provider,
    mux_playback_id: m.mux_playback_id,
    mux_status: m.mux_status,
    r2_key: m.r2_key,
  }));
}
